import { currentLanguage } from '@/locale/translator';
import type { FormContactMethod, IPublicationData, PublicationType } from '@/types/publication';
import type { Order } from '@/types/types';
import { request, type RequestOptions } from './client';
import type { TemplateMeta } from '@/types/templateMeta';

function fixDirectLink(directlink: string | undefined): string | undefined {
  if (!directlink) return directlink;
  const trimmedLink = directlink.trim();
  if (trimmedLink && !trimmedLink.startsWith('http')) {
    return `https://${trimmedLink}`;
  }
  return trimmedLink;
}

export function createOrderDraft(
  body: Partial<Order>,
  params: Record<string, any> = {},
  requestOptions: RequestOptions = {}
): Promise<{ data: Order }> {
  const playload = { ...body, directlink: fixDirectLink(body.directlink), apply_url: fixDirectLink(body.apply_url) };
  return request('POST', '/v1/account/order/draft', params, playload, requestOptions);
}
export function updateOrderDraft(
  orderId: number | string,
  body: Partial<Order>,
  params: Record<string, string | number>
): Promise<{ data: Order }> {
  const playload = { ...body, directlink: fixDirectLink(body.directlink), apply_url: fixDirectLink(body.apply_url) };

  return request('PUT', `/v1/account/order/draft/${orderId}`, params, playload);
}

// does not send template_values: create evals from/default against the order (reset snapshot).
// picker demo_data must never land on a publication.
export function createCustomerPublication(body: {
  template: string;
  variation?: string;
  name: string;
  order_id: number;
  isFunnel?: boolean;
  optimizeContent?: boolean;
  generateQuestions?: boolean;
  formContactMethod?: FormContactMethod;
}): Promise<{ data: PublicationType }> {
  const { isFunnel, optimizeContent, generateQuestions, formContactMethod, ...rest } = body;
  let requestBody: {
    template: string;
    variation?: string;
    name: string;
    order_id: number;
    funnel_enabled?: boolean;
    form_contact_method?: FormContactMethod;
  } = { ...rest };

  // optimize_content and generate_questions both apply to funnel and application-form
  // publications alike - the qualifying-question infrastructure they reuse is shared, see
  // GetFormGenerateQuestionsTaskAction on the backend
  if (optimizeContent !== false) {
    requestBody = { ...requestBody, optimize_content: optimizeContent } as any;
  }

  if (generateQuestions !== false) {
    requestBody = { ...requestBody, generate_questions: generateQuestions } as any;
  }

  if (isFunnel) {
    requestBody.funnel_enabled = true;
    requestBody.form_contact_method = formContactMethod;
  }

  return request('POST', `/v1/account/publications`, {}, requestBody);
}

export function getCustomerPublication(pubkey: string): Promise<{ data: PublicationType }> {
  return request('GET', `/v1/account/publications/${pubkey}`, {});
}

export function updateCustomerPublication(
  pubkey: string,
  body: Partial<IPublicationData>
): Promise<{ data: PublicationType }> {
  return request('PUT', `/v1/account/publications/${pubkey}`, {}, body);
}

export function getPublicationTemplates<T>(params?: Record<string, string | number>): Promise<{ data: T }> {
  return request('GET', `/v1/account/publication/templates`, { locale: currentLanguage, ...params });
}

export function getPublicationTemplate(
  templateKey: string,
  variation?: string | null
): Promise<{ data: TemplateMeta }> {
  return request('GET', `/v1/account/publication/templates/${templateKey}`, {
    locale: currentLanguage,
    ...(variation ? { variation } : {}),
  });
}

// preview_image returns JSON (not binary): { data: { '800x600': <proxy url> }, image: {...} }.
// pass a variation to get that variation's preview instead of the base template's.
export function getPublicationTemplatePreviewImage(
  templateKey: string,
  variation?: string
): Promise<{ data: { '800x600': string }; image: unknown }> {
  return request('GET', `/v1/account/publication/templates/${templateKey}/preview_image`, {
    locale: currentLanguage,
    ...(variation ? { variation } : {}),
  });
}

export function fetchInteractionModules(): Promise<Record<string, any>> {
  return request('GET', '/v1/publication/pops/modules');
}

// transform_content fills autogen options (extract vs rewrite from optimize_content on the publication)
export function getPublicationTaskStatus(
  pubkey: string,
  task: 'transform_content' = 'transform_content'
): Promise<{
  data: Record<string, unknown>;
  task: { status_string: 'in_progress' | 'success' | 'error' } | null;
}> {
  return request('GET', `/v1/account/publications/${pubkey}/tasks/${task}`);
}
